import React from 'react';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../legalConstants';
import { Link } from 'react-router-dom';

const FRONTEND_LICENSES = [
  { name: 'React', license: 'MIT', url: 'https://github.com/facebook/react' },
  { name: 'React DOM', license: 'MIT', url: 'https://github.com/facebook/react' },
  { name: 'React Router DOM', license: 'MIT', url: 'https://github.com/remix-run/react-router' },
  { name: 'Redux Toolkit', license: 'MIT', url: 'https://github.com/reduxjs/redux-toolkit' },
  { name: 'React Redux', license: 'MIT', url: 'https://github.com/reduxjs/react-redux' },
  { name: 'Axios', license: 'MIT', url: 'https://github.com/axios/axios' },
  { name: 'Bootstrap', license: 'MIT', url: 'https://github.com/twbs/bootstrap' },
  { name: 'React Bootstrap', license: 'MIT', url: 'https://github.com/react-bootstrap/react-bootstrap' },
  { name: 'Chart.js / react-chartjs-2', license: 'MIT', url: 'https://github.com/chartjs/Chart.js' },
  { name: 'DOMPurify', license: 'Apache-2.0 / MPL-2.0', url: 'https://github.com/cure53/DOMPurify' },
  { name: '@react-oauth/google', license: 'MIT', url: 'https://github.com/MomenSherif/react-oauth' },
  { name: '@marsidev/react-turnstile', license: 'MIT', url: 'https://github.com/marsidev/react-turnstile' },
  { name: '@dnd-kit/core & sortable', license: 'MIT', url: 'https://github.com/clauderic/dnd-kit' },
  { name: 'react-icons', license: 'MIT', url: 'https://github.com/react-icons/react-icons' },
  { name: 'moment', license: 'MIT', url: 'https://github.com/moment/moment' },
  { name: 'js-cookie', license: 'MIT', url: 'https://github.com/js-cookie/js-cookie' },
  { name: 'jwt-decode', license: 'MIT', url: 'https://github.com/auth0/jwt-decode' },
  { name: 'xlsx (SheetJS)', license: 'Apache-2.0', url: 'https://github.com/SheetJS/sheetjs' },
  { name: 'html2canvas', license: 'MIT', url: 'https://github.com/niklasvh/html2canvas', note: 'Rich text editor PDF export (DOM to canvas).' },
  { name: 'jsPDF', license: 'MIT', url: 'https://github.com/parallax/jsPDF', note: 'Rich text editor PDF export (multi-page JPEG in PDF).' },
  {
    name: 'docx',
    license: 'MIT',
    url: 'https://github.com/dolanmiu/docx',
    note: 'Rich text editor Word (.docx) export.',
  },
  {
    name: '@noble/ciphers',
    license: 'MIT',
    url: 'https://github.com/paulmillr/noble-ciphers',
    note: 'Client-side AES-GCM for encryption.',
  },
];

const BACKEND_LICENSES = [
  { name: 'Express', license: 'MIT', url: 'https://github.com/expressjs/express' },
  { name: 'Mongoose', license: 'MIT', url: 'https://github.com/Automattic/mongoose' },
  { name: 'bcryptjs', license: 'MIT', url: 'https://github.com/dcodeIO/bcrypt.js' },
  { name: 'jsonwebtoken', license: 'MIT', url: 'https://github.com/auth0/node-jsonwebtoken' },
  { name: 'Helmet', license: 'MIT', url: 'https://github.com/helmetjs/helmet' },
  { name: 'CORS', license: 'MIT', url: 'https://github.com/expressjs/cors' },
  { name: 'dotenv', license: 'BSD-2-Clause', url: 'https://github.com/motdotla/dotenv' },
  { name: 'express-rate-limit', license: 'MIT', url: 'https://github.com/express-rate-limit/express-rate-limit' },
  { name: 'Multer', license: 'MIT', url: 'https://github.com/expressjs/multer' },
  { name: 'Nodemailer', license: 'MIT-0', url: 'https://github.com/nodemailer/nodemailer' },
  { name: 'Sharp', license: 'Apache-2.0', url: 'https://github.com/lovell/sharp' },
  { name: 'isomorphic-dompurify', license: 'MIT', url: 'https://github.com/kkomelin/isomorphic-dompurify' },
  {
    name: '@aws-sdk/client-s3 & s3-request-presigner',
    license: 'Apache-2.0',
    url: 'https://github.com/aws/aws-sdk-js-v3',
    note: 'S3-compatible client and presigned URLs for private Cloudflare R2 storage.',
  },
  { name: 'google-auth-library', license: 'Apache-2.0', url: 'https://github.com/googleapis/google-auth-library-nodejs' },
  { name: 'archiver', license: 'MIT', url: 'https://github.com/archiverjs/node-archiver' },
  { name: 'ffmpeg-static', license: 'GPL-3.0-or-later', note: 'Copyleft — source available from upstream; used for media processing where enabled.' },
  { name: 'cookie-parser', license: 'MIT', url: 'https://github.com/expressjs/cookie-parser' },
  { name: 'express-session', license: 'MIT', url: 'https://github.com/expressjs/session' },
  { name: 'body-parser', license: 'MIT', url: 'https://github.com/expressjs/body-parser' },
];

const THIRD_PARTY_SERVICES = [
  { name: 'Cloudflare Turnstile', purpose: 'Human verification on sign-in, sign-up, and contact forms' },
  { name: 'Google Sign-In', purpose: 'Optional OAuth authentication' },
  { name: 'Cloudflare R2 / AWS S3-compatible storage', purpose: 'Media and file hosting where configured' },
];

function LicenseTable({ items }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="legal-license-table">
        <thead>
          <tr>
            <th>Component</th>
            <th>License</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.name}>
              <td>
                {item.url ? (
                  <a href={item.url} className="legal-inline-link" target="_blank" rel="noopener noreferrer">
                    {item.name}
                  </a>
                ) : (
                  item.name
                )}
              </td>
              <td>{item.license}</td>
              <td>{item.note || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OpenSourcePage() {
  return (
    <LegalLayout
      title="Open Source Licenses"
      subtitle={`Third-party software and services used to build ${APP_NAME}.`}
    >
      <LegalLastUpdated date="October 31, 2026" />

      <LegalSection title="Overview">
        <p>
          {APP_NAME} is built with open-source software and third-party services. We are grateful to the developers and
          communities who maintain these projects. This page lists major dependencies and their licenses as of the last
          update date. The complete dependency tree may include additional packages; refer to project lockfiles for
          exhaustive listings.
        </p>
        <p>
          {APP_NAME} application code is proprietary to its operator unless otherwise stated. Project metadata may
          reference ISC or other licenses for specific packages within the repository.
        </p>
      </LegalSection>

      <LegalSection title="Technological dependencies">
        <LicenseTable items={FRONTEND_LICENSES} />
      </LegalSection>

      <LegalSection title="Built-in rich text export">
        <p>
          The reusable rich text / markdown editor can export content as HTML, Word-compatible <code>.doc</code>{' '}
          (HTML packaged for Microsoft Word and compatible apps), Office Open XML <code>.docx</code> (via{' '}
          <a href="https://github.com/dolanmiu/docx" className="legal-inline-link" target="_blank" rel="noopener noreferrer">
            docx
          </a>
          ), and PDF. HTML and Word exports preserve structure, colors, and typography by inlining computed
          presentation styles from the editor at export time. PDF export renders the same
          styled HTML to a canvas and builds a multi-page PDF using{' '}
          <a href="https://github.com/niklasvh/html2canvas" className="legal-inline-link" target="_blank" rel="noopener noreferrer">
            html2canvas
          </a>{' '}
          (MIT) and{' '}
          <a href="https://github.com/parallax/jsPDF" className="legal-inline-link" target="_blank" rel="noopener noreferrer">
            jsPDF
          </a>{' '}
          (MIT). Exported HTML is sanitized with the existing DOMPurify dependency. Remote images in documents must allow
          cross-origin loading to appear in PDF exports.
        </p>
      </LegalSection>

      <LegalSection title="Backend dependencies">
        <LicenseTable items={BACKEND_LICENSES} />
        <p>
          <strong>GPL notice:</strong> Where GPL-licensed components (such as ffmpeg-static) are used, corresponding
          source code and license texts are available from the upstream projects linked above. If you distribute
          binaries incorporating GPL components, you may have additional obligations to provide source or offer
          corresponding source.
        </p>
        <p>
          <strong>Sharp / libvips:</strong> Image processing may include native libraries under Apache-2.0 and
          LGPL-3.0-or-later. See the Sharp project documentation for attribution details.
        </p>
      </LegalSection>

      <LegalSection title="Third-party services">
        <ul>
          {THIRD_PARTY_SERVICES.map((svc) => (
            <li key={svc.name}>
              <strong>{svc.name}</strong> — {svc.purpose}
            </li>
          ))}
        </ul>

        <strong>Important notice:</strong> {' '}
        <p>
        This list identifies major third-party components used by the service and may not include every transitive dependency. Each component remains subject to its respective license.
        </p>
      </LegalSection>

      <LegalSection title="Trademarks">
        <p>
          {APP_NAME}, the {APP_NAME} logo, and related branding are trademarks of their respective owner. Third-party
          names and logos are property of their respective owners. Use of third-party trademarks does not imply
          endorsement.
        </p>
      </LegalSection>

      <LegalSection title="Corrections">
        <p>
          If you believe attribution is missing or incorrect, contact{' '}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
